import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import {
  V4UUID,
  allWorkspacesStateSingleton,
  dbRef,
  getWorkspacesStateOnce
} from './database';
import { set, update, remove, onValue, query } from 'firebase/database';
import authStore from './auth';
import { Node } from '@/modules/nodesCanvasModule/components/storeClasses/node';
import { Service } from '@/modules/nodesCanvasModule/components/storeClasses/service';
import { ConnectionDetails } from '@/modules/controlCenterModule/store';
import { GitTokenDetails } from '@/gitIntegration/gitAuth';
import { GitWorkspaceDetails } from '@/gitIntegration/gitRepo';
import { LaunchFile } from '@/modules/controlCenterModule/components/robotConnection/loadLaunchFilesStrategy';

const checkUserAdmin = (): boolean => {
  const ref = dbRef(`${authStore.uid}/admin`);
  let admin: boolean = false;
  onValue(ref, (snapshot: any) => {
    admin = snapshot.val() || false;
  });
  return admin;
};

const getAllPackagesInWorkspace = (
  workspaceId: V4UUID
): Record<string, PackageT> | undefined => {
  const workspaceSnap = allWorkspacesStateSingleton()?.info;
  if (!workspaceSnap) return;
  if (!workspaceSnap[workspaceId] || !workspaceSnap[workspaceId].packages)
    return {};
  for (let pkg of Object.entries<any>(workspaceSnap[workspaceId].packages)) {
    workspaceSnap[workspaceId].packages[pkg[1].id] = PackageT.fromJSON(pkg[1]);
  }
  return workspaceSnap[workspaceId].packages;
};

const getAllPackagesAsync = async (
  workspaceId: V4UUID
): Promise<Record<string, PackageT> | undefined> => {
  const workspaceSnap = await getWorkspacesStateOnce();
  if (!workspaceSnap) return;
  if (!workspaceSnap[workspaceId] || !workspaceSnap[workspaceId].packages)
    return {};
  for (let pkg of Object.entries<any>(workspaceSnap[workspaceId].packages)) {
    workspaceSnap[workspaceId].packages[pkg[1].id] = PackageT.fromJSON(pkg[1]);
  }
  return workspaceSnap[workspaceId].packages;
};

const getPackage = (
  workspaceId: V4UUID,
  packageId: V4UUID
): PackageT | undefined => {
  // const pack = allWorkspaces.info[workspaceId]?.packages[packageId];
  // const snap = await allWorkspaces.info;
  // if (!snap) return;
  const pack =
    allWorkspacesStateSingleton()?.info?.[workspaceId]?.packages?.[packageId];
  if (!pack) return;
  if (pack.nodes === undefined) pack.nodes = [];
  if (pack.services === undefined) pack.services = [];
  if (pack.topics === undefined) pack.topics = [];
  if (pack.favouriteElems === undefined) pack.favouriteElems = [];
  if (pack.msgs === undefined) pack.msgs = [];
  if (pack.nodeTemplates === undefined) pack.nodeTemplates = [];
  return pack;
};

const removeDbPackage = (workspaceId: V4UUID, packageId: V4UUID) => {
  const ref = dbRef(
    `${authStore.uid}/workspaces/${workspaceId}/packages/${packageId}`
  );
  return remove(ref);
};

const savePackage = (
  workspaceId: V4UUID,
  packageId: V4UUID,
  data: PackageT
) => {
  const ref = dbRef(
    `${authStore.uid}/workspaces/${workspaceId}/packages/${packageId}`
  );
  const dataToSave = JSON.parse(JSON.stringify(data));
  return update(ref, dataToSave);
};

const setNodesInPackage = (
  workspaceID: V4UUID,
  packageID: V4UUID,
  data: Node[]
) => {
  const ref = dbRef(
    `${authStore.uid}/workspaces/${workspaceID}/packages/${packageID}/nodes`
  );
  const dataToSave = JSON.parse(JSON.stringify(data));
  return set(ref, dataToSave);
};

const setServicesInPackage = (
  workspaceID: V4UUID,
  packageID: V4UUID,
  data: Service[]
) => {
  const ref = dbRef(
    `${authStore.uid}/workspaces/${workspaceID}/packages/${packageID}/services`
  );
  const dataToSave = JSON.parse(JSON.stringify(data));
  return set(ref, dataToSave);
};

const getUnparsedNodesInPackage = (
  workspaceID: V4UUID,
  packageID: V4UUID
): any[] | undefined => {
  const db = allWorkspacesStateSingleton();
  return db?.info?.[workspaceID]?.packages?.[packageID]?.nodes;
};

const getUnparsedServicesInPackage = (
  workspaceID: V4UUID,
  packageID: V4UUID
): any[] | undefined => {
  const db = allWorkspacesStateSingleton();
  return db?.info?.[workspaceID]?.packages?.[packageID]?.services;
};
/////////////////////////////////////////////////////////////////
//                         Launch Files                        //
/////////////////////////////////////////////////////////////////
/**
 * Saves the given launch files to the database. Overwrites any previous
 * launch files stored in the database
 * @param workspaceId
 * @param launchFiles
 * @returns
 */
const saveLaunchFiles = (workspaceId: V4UUID, launchFiles: LaunchFile[]) => {
  const ref = dbRef(`${authStore.uid}/workspaces/${workspaceId}/launchFiles`);
  return set(ref, JSON.parse(JSON.stringify(launchFiles)));
};

/**
 * Adds the given launch files to the database. Does not add duplicates.
 * @param workspaceId
 * @param launchFiles
 * @returns
 */
const addLaunchFiles = (workspaceId: V4UUID, launchFiles: LaunchFile[]) => {
  const db = allWorkspacesStateSingleton();
  const ref = dbRef(`${authStore.uid}/workspaces/${workspaceId}/launchFiles`);
  if (db.info?.[workspaceId] && db.info?.[workspaceId].launchFiles) {
    launchFiles = [
      ...new Set(launchFiles.concat(db.info?.[workspaceId].launchFiles))
    ];
  }
  return set(ref, JSON.parse(JSON.stringify(launchFiles)));
};

const getWorkspaceLaunchFiles = (workspaceId: V4UUID) => {
  const db = allWorkspacesStateSingleton();
  if (db.info?.[workspaceId] && db.info?.[workspaceId].launchFiles)
    return db.info?.[workspaceId].launchFiles;
  return [];
};
/////////////////////////////////////////////////////////////////
//                       Connection Data                       //
/////////////////////////////////////////////////////////////////

const saveWorkspaceConnection = (
  workspaceId: V4UUID,
  connectionDetails: ConnectionDetails
) => {
  const db = allWorkspacesStateSingleton();
  const ref = dbRef(
    `${authStore.uid}/workspaces/${workspaceId}/connections/${connectionDetails.id}`
  );
  if (
    !db.info?.[workspaceId] ||
    !db.info?.[workspaceId].connections ||
    !db.info?.[workspaceId].connections?.[connectionDetails.id]
  ) {
    return set(ref, JSON.parse(JSON.stringify(connectionDetails)));
  }
  return update(ref, JSON.parse(JSON.stringify(connectionDetails)));
};

const getAllWorkspaceConnections = (workspaceId: V4UUID): any[] => {
  const db = allWorkspacesStateSingleton();
  if (db.info?.[workspaceId]) return db.info?.[workspaceId].connections;
  return [];
};

const getWorkspaceConnection = (
  workspaceId: V4UUID,
  connectionId: V4UUID
): ConnectionDetails | undefined => {
  const db = allWorkspacesStateSingleton();
  if (db.info?.[workspaceId].connections?.[connectionId])
    return ConnectionDetails.fromJSON(
      db.info?.[workspaceId].connections?.[connectionId]
    );
  return undefined;
};

/////////////////////////////////////////////////////////////////
//                       Git Persistence                       //
/////////////////////////////////////////////////////////////////

export const storeGitCode = (details: GitTokenDetails) => {
  const ref = dbRef(`${authStore.uid}/git`);
  let gitDbInfo: any = {};
  onValue(ref, (snapshot: any) => {
    gitDbInfo = snapshot.val();
  });
  if (!gitDbInfo.access_token) {
    return set(ref, JSON.parse(JSON.stringify(details)));
  }
  return update(ref, JSON.parse(JSON.stringify(details)));
};

export const getGitCode = (): GitTokenDetails => {
  const ref = dbRef(`${authStore.uid}/git`);
  let gitDbInfo: any = {};
  onValue(ref, (snapshot: any) => {
    gitDbInfo = snapshot.val();
  });
  return gitDbInfo;
};

export const saveGitWorkspaceDetails = (
  workspaceId: V4UUID,
  gitDetails: GitWorkspaceDetails
) => {
  const db = allWorkspacesStateSingleton();
  const ref = dbRef(`${authStore.uid}/workspaces/${workspaceId}/git`);
  if (!db.info?.[workspaceId] || !db.info?.[workspaceId].git) {
    return set(ref, JSON.parse(JSON.stringify(gitDetails)));
  }
  return update(ref, JSON.parse(JSON.stringify(gitDetails)));
};

export const getGitWorkspaceDetails = (
  workspaceId: V4UUID
): GitWorkspaceDetails | undefined => {
  const ref = dbRef(`${authStore.uid}/workspaces/${workspaceId}/git`);
  let gitWsInfo: any = {};
  onValue(ref, (snapshot: any) => {
    gitWsInfo = snapshot.val();
  });
  if (gitWsInfo && gitWsInfo.repoId && gitWsInfo.repoName) {
    return gitWsInfo as GitWorkspaceDetails;
  }
  return undefined;
};

export {
  getAllPackagesInWorkspace,
  getPackage,
  savePackage,
  setNodesInPackage,
  getUnparsedNodesInPackage,
  getUnparsedServicesInPackage,
  setServicesInPackage,
  removeDbPackage,
  saveWorkspaceConnection,
  getAllWorkspaceConnections,
  getWorkspaceConnection,
  getAllPackagesAsync,
  saveLaunchFiles,
  getWorkspaceLaunchFiles,
  checkUserAdmin,
  addLaunchFiles
};
